/**
 * Rig socket re-seat probe (E2.4.S1, convention d) — when the real CHR-026 rig
 * renders, E2.2's equipment attachments must ride the rig's BONE sockets instead of
 * the capsule-era group-frame transforms. This probe resolves, per equipment slot,
 * whether the loaded skeleton carries the CHR-026 §4.2 bone for that slot — a hit
 * yields a rig-mode (bone) attachment; a miss falls back, PER SOCKET, to E2.2's
 * capsule transform (graceful — the recorded E2.2 deferral: "re-seated on CHR-026
 * bone sockets at E2.4").
 *
 * FRONTEND-LOCAL by design: the slot→bone-socket-name map is the client's
 * provisional rendering convention, not a `shared/` constant (promotion = T3). The
 * AD-181 interim 24-joint Meshy rig uses its own bone names that will NOT match
 * §4.2 ⇒ every slot misses ⇒ every socket keeps E2.2's capsule transform (exactly
 * today's behaviour). The real bone-name reconciliation lands at the E2.5/E4.4
 * re-import; the bone-mode local grip/attach offsets here are PROVISIONAL zero
 * offsets calibrated per prop family against the delivered bind pose and the
 * idle/locomotion/dodge visual evidence wave.
 * Most committed armour PLACEHOLDERS are a different convention: their vertices
 * are already authored in full-avatar root space, so rig placement must keep
 * them on the avatar root with no second body-region offset. The fitted head
 * candidates are the deliberate exception: they are authored in delivered
 * `head`-socket local space so a rigid cap follows the animated skull.
 *
 * Consumes E2.2's socket provider UNCHANGED (the story's dependency contract):
 * `EQUIPMENT_SOCKETS`/`SocketTransform` are imported, never redefined. Node-safe:
 * no `three` import; the skeleton is an abstract `BoneLookup`, so the probe runs
 * under `node --test` (AD-120).
 */

import { EQUIPMENT_SOCKETS, type SocketTransform } from '../equipment/sockets.js';
import { heldGripFor, heldGripSeat, leftHeldGripFor, leftHeldGripSeat } from './heldGrip.js';

/**
 * Convention (d): equipment slot (`EQUIPMENT_SLOTS`) → CHR-026 §4.2 bone-socket
 * name. §4.2 provides right hand / left hand / back / hips-belt / head / shoulders
 * / chest / both forearms / both feet / waist; the live equipment enum keys those
 * as below. `trinket` has no visible worn socket (matches E2.2's map). PROVISIONAL
 * names — re-seated onto the delivered rig's actual bone names at E2.4.
 */
export const RIG_SOCKET_BONE: Readonly<Record<string, string>> = {
  mainhand: 'RightHand', // animated right-hand joint — weapon grip (§4.2)
  offhand: 'LeftHand', // animated left-hand joint — offhand / shield (§4.2)
  head: 'Head', // animated head joint — helm (§4.2)
  chest: 'Spine', // animated upper-spine joint — rigid torso shell (§4.2)
  hands: 'RightForeArm', // multipart placement resolves both animated forearms
  legs: 'Hips', // multipart placement resolves both animated upper legs
  feet: 'RightFoot', // multipart placement resolves both animated feet
  // `trinket` intentionally absent — no visible worn mesh in v1 (matches E2.2).
};

/**
 * The current chest/hands/legs/feet placeholders remain full-avatar ROOT-SPACE
 * meshes. Parenting one to a body bone applies the bone's body-space transform
 * again. The refitted head binaries no longer use this convention: they are
 * explicitly socket-local and therefore resolve through the animated head bone.
 */
export const RIG_ROOT_SPACE_EQUIPMENT_SLOTS: ReadonlySet<string> = new Set();

/** Root-space placeholder placement for slices that already fit the v2 bind body. */
export const RIG_ROOT_SPACE_ATTACH_OFFSET: SocketTransform = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: 1,
};

/**
 * The rigid chest bases were authored against the earlier placeholder silhouette.
 * Slot-isolated v2 captures show the complete slices are about 15%
 * too broad; scaling them around the avatar root therefore needs compensating
 * upward translations to keep their authored head/shoulder heights. A small +Z
 * seat brings the shell onto the adopted body's front surface. Hands/legs/feet
 * already match and stay untouched.
 */
const RIG_ROOT_CHEST_ATTACH_OFFSET: SocketTransform = {
  position: [0, 0.1, 0.08],
  rotation: [0, 0, 0],
  scale: 0.85,
};

/** Per-base fit map for complete rigid armour slices on the avatar root. */
export function rigRootSpaceAttachOffset(model: string): SocketTransform {
  if (model === 'light-chest' || model === 'metal-chest') return RIG_ROOT_CHEST_ATTACH_OFFSET;
  return RIG_ROOT_SPACE_ATTACH_OFFSET;
}

/** The minimal shape the probe reads off a resolved bone — a named `THREE.Object3D`/`Bone`. */
export interface RigBone {
  readonly name?: string;
}

/** Find a bone/node by name in the loaded skeleton (a real skeleton or a test fake). */
export type BoneLookup = (name: string) => RigBone | null | undefined;

/** A per-slot socket resolution: rig-mode (bone) on a hit, capsule fallback on a miss. */
export type RigSocketResolution =
  | {
      slot: string;
      mode: 'bone';
      /** The resolved skeleton bone the attachment parents to. */
      bone: RigBone;
      /** The §4.2 bone name that resolved. */
      boneName: string;
      /** PROVISIONAL bone-local offset (re-tuned per-socket at E2.4/AP5). */
      transform: SocketTransform;
    }
  | {
      slot: string;
      mode: 'capsule';
      /** E2.2's capsule-era group-frame transform, kept verbatim on a bone miss. */
      transform: SocketTransform;
    };

/**
 * PROVISIONAL bone-local attach offset for rig-mode sockets — a zero offset (attach
 * at the bone origin). The real per-weapon grip / per-armour fit pose is an AP5 +
 * E2.4 re-seat concern; a single honest zero here is deliberately un-tuned.
 */
/**
 * The adopted v2 skeleton is authored in centimetres under an Armature scale of
 * 0.01, while equipment bases are authored in metres. A prop parented below the
 * Armature therefore needs the reciprocal local scale to retain its metre size.
 */
export const RIG_BONE_UNIT_SCALE = 100;

const RIG_BONE_ATTACH_OFFSET: SocketTransform = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: RIG_BONE_UNIT_SCALE,
};

/**
 * The delivered blade bases extend from the grip along local +Y. The right-hand
 * bind basis points that axis down. This measured pose sends +Y upward, outward,
 * and slightly forward while preserving the grip origin, like the approved AA-007
 * family reference. Still the offhand dagger's seat (LeftHand, no grip morph there).
 *
 * It WAS the sword's mainhand seat until AD-838: an open palm the hilt passed
 * through (the owner's 2026-10-23 close-up). Kept, exported, as `PRE_HOLD_*` so the
 * grip check can prove it still fails on exactly what the owner saw.
 */
export const PRE_HOLD_BLADE_ATTACH_OFFSET: SocketTransform = {
  position: [7.486927961, 17.538401458, 2.098074546],
  rotation: [-0.2337, -0.437, 1.8],
  scale: RIG_BONE_UNIT_SCALE,
};
const RIG_BLADE_ATTACH_OFFSET: SocketTransform = PRE_HOLD_BLADE_ATTACH_OFFSET;

/**
 * The procedural bow base spans both directions around its centre grip. The
 * measured left-hand bind basis sends its zero-rotation upper limb inward across
 * the torso, hiding half the silhouette. The delivered hand bone ends at the wrist,
 * so the measured 9 cm local +Y seat moves the centre grip into the palm while the
 * calibrated rotation preserves an outward, readable held-bow silhouette.
 */
export const PRE_GRIP_BOW_ATTACH_OFFSET: SocketTransform = {
  position: [0, 9, 0],
  rotation: [-1.2014, 1.3105, 1.94],
  scale: RIG_BONE_UNIT_SCALE,
};
/**
 * The open-hand bow seat above is what shipped until `q-bow-left-hand-grip`: kept, exported,
 * so the grip check can prove it still fails. The bow is now seated by `LEFT_HELD_GRIPS`.
 */
const RIG_BOW_ATTACH_OFFSET: SocketTransform = PRE_GRIP_BOW_ATTACH_OFFSET;

/**
 * Right-hand held props close a right fist on their hilt (`grip-r-fist`, or `grip-r-haft` for a
 * row that names it): the seat comes from `HELD_GRIPS` (`heldGrip.ts`, data), never a per-base
 * offset here. The rig has no finger joints, so a morph is the only closed hand it owns, and every
 * prop with a row gets one (AD-838; the fists themselves, q-grip-fist-inner-hand-see-through).
 */
export function usesRightHandGripMorph(model: string | null): boolean {
  return heldGripFor(model) !== null;
}

/**
 * The mainhand (RightHand) seat for a rendered mesh of an archetype: its held grip
 * when the table has one, else the per-base calibration below.
 */
export function rigMainhandAttachOffset(archetype: string, mesh?: string | null): SocketTransform {
  const grip = heldGripFor(mesh, archetype);
  return grip === null ? rigBoneAttachOffset(archetype) : heldGripSeat(grip);
}

/**
 * Left-hand held props (the left-lead bow) close the authored `grip-l` fist on their riser,
 * seated from `LEFT_HELD_GRIPS` (`heldGrip.ts`, data); `q-bow-left-hand-grip`.
 */
export function usesLeftHandGripMorph(model: string | null): boolean {
  return leftHeldGripFor(model) !== null;
}

/**
 * The LeftHand seat for a rendered mesh of a left-lead archetype: its held grip when the table
 * has one, else the per-base calibration below.
 */
export function rigLeftHandAttachOffset(archetype: string, mesh?: string | null): SocketTransform {
  const grip = leftHeldGripFor(mesh, archetype);
  return grip === null ? rigBoneAttachOffset(archetype) : leftHeldGripSeat(grip);
}

/**
 * The shield seats as they shipped until `q-shield-clips-body-rear-view` (owner W44 pack review,
 * 2026-10-28: "walking away from camera the shield passes through the body"). Both put the face
 * (local +Z) along the LeftHand basis +Z, which the hanging left arm turns to face BACKWARD, so
 * the 72 cm heater and the 48 cm buckler lay ACROSS the body from the hand, through the hips and
 * back in every idle frame (measured: offhandClearance.test.ts). Kept, exported, so that check can
 * prove they still fail.
 */
export const PRE_SPIN_SHIELD_ATTACH_OFFSET: SocketTransform = {
  position: [0, -8, 8],
  rotation: [0, 0, Math.PI],
  scale: RIG_BONE_UNIT_SCALE,
};
export const PRE_SPIN_BUCKLER_ATTACH_OFFSET: SocketTransform = {
  position: [0, 10, 7],
  rotation: [0, 0, 0],
  scale: RIG_BONE_UNIT_SCALE,
};

/**
 * The shield sources face local +Z; their rear handle runs along local +Y, rear face at Z=-6.8 cm,
 * centred at Y=+4 cm (heater) / +2 cm (buckler). The heater's local +Y points down the LeftHand
 * basis until a Z half-turn restores point-down. Then a quarter-turn about the bone's own +Y (the
 * forearm) points the face OUTWARD, so the plate hangs along the body's left side instead of
 * across it, and a tilt about bone X (heater -10°, buckler -20°) trims the rim's crossings in the
 * sword's and shield's attacks. Turn and tilt were chosen by measurement over every mesh of each
 * archetype and every clip a shield-bearer plays (H-1546: turns 75–120° × 15 tilts; crossings
 * 31,844 ⇒ 2,670 heater/round, 6,016 ⇒ 393 bucklers; no tilt helps the quick-strike ability,
 * owed to q-shield-carry-attack-clips). Rotation reads tilt · turn · base (Euler XYZ). Each position
 * keeps the handle's rear face on the same palm point as the shipped seat (`p' = p + R·g − R'·g`),
 * so the hand still holds the grip. offhandClearance.test.ts pins all of it.
 */
const RIG_SHIELD_ATTACH_OFFSET: SocketTransform = {
  position: [6.8, -8.06, 0.51],
  rotation: [(-10 * Math.PI) / 180, Math.PI / 2, Math.PI],
  scale: RIG_BONE_UNIT_SCALE,
};

const RIG_BUCKLER_ATTACH_OFFSET: SocketTransform = {
  position: [6.8, 10.12, 0.88],
  rotation: [(-20 * Math.PI) / 180, Math.PI / 2, 0],
  scale: RIG_BONE_UNIT_SCALE,
};

/**
 * The same grip turned end-over-end: the prop rotated half a turn about its OWN local X
 * (R' = R · Rx(π)), so its local +Y axis — the haft, grip → head — points the opposite
 * way while the grip origin stays in the palm. Position and scale are unchanged. Pure
 * Euler-XYZ algebra (three.js's order and extraction), so it stays node-safe.
 */
export function reverseGrip(transform: SocketTransform): SocketTransform {
  const [a, b, c] = transform.rotation;
  const [ca, sa, cb, sb, cc, sc] = [
    Math.cos(a),
    Math.sin(a),
    Math.cos(b),
    Math.sin(b),
    Math.cos(c),
    Math.sin(c),
  ];
  // The rotation matrix of Euler XYZ (a, b, c) — only the entries the extraction reads.
  const m11 = cb * cc;
  const m12 = -cb * sc;
  const m13 = sb;
  const m22 = ca * cc - sa * sb * sc;
  const m23 = -sa * cb;
  const m32 = sa * cc + ca * sb * sc;
  const m33 = ca * cb;
  // · Rx(π) negates the 2nd and 3rd columns; re-extract Euler XYZ from that matrix.
  const y = Math.asin(Math.max(-1, Math.min(1, -m13)));
  const gimbal = Math.abs(m13) >= 0.9999999;
  const x = gimbal ? Math.atan2(-m32, -m22) : Math.atan2(m23, -m33);
  const z = gimbal ? 0 : Math.atan2(m12, m11);
  return { position: transform.position, rotation: [x, y, z], scale: transform.scale };
}

/** Per-base grip calibration behind stable equipment model ids. */
export function rigBoneAttachOffset(model: string): SocketTransform {
  if (model === 'sword-1h-base' || model === 'dagger-offhand-base') {
    return RIG_BLADE_ATTACH_OFFSET;
  }
  if (model === 'bow-base') return RIG_BOW_ATTACH_OFFSET;
  if (model === 'shield-base') return RIG_SHIELD_ATTACH_OFFSET;
  if (model === 'buckler-base') return RIG_BUCKLER_ATTACH_OFFSET;
  return RIG_BONE_ATTACH_OFFSET;
}

/**
 * GLTFLoader sanitises Object3D names through `PropertyBinding.sanitizeNodeName`:
 * dots are removed (`hand.R` → `handR`). Probe the locked raw alias first, then
 * that measured runtime spelling. Keeping the canonical name first also supports
 * fixtures and any future loader that preserves names verbatim.
 */
function runtimeBoneNames(canonical: string): readonly string[] {
  const sanitised = canonical.replaceAll('.', '');
  return sanitised === canonical ? [canonical] : [canonical, sanitised];
}

/** Resolve a canonical delivered joint/attachment alias through loader sanitisation. */
export function resolveRigBone(bone: BoneLookup, canonical: string): RigBone | null | undefined {
  for (const candidate of runtimeBoneNames(canonical)) {
    const found = bone(candidate);
    if (found !== null && found !== undefined) return found;
  }
  return null;
}

/**
 * Probe the loaded skeleton for each equipment slot's CHR-026 §4.2 bone. A hit ⇒ a
 * rig-mode attachment on that bone; a miss ⇒ E2.2's capsule transform for the slot,
 * kept verbatim (per-socket graceful fallback). A slot with neither a rig bone nor a
 * capsule transform is omitted (no attachment — the graceful path). Pure: the caller
 * (E2.4.S2) decides how to parent based on `mode`; this story exposes the mapping.
 */
export function resolveRigSockets(
  bone: BoneLookup,
  capsule: Readonly<Record<string, SocketTransform>> = EQUIPMENT_SOCKETS,
): Record<string, RigSocketResolution> {
  const out: Record<string, RigSocketResolution> = {};
  for (const [slot, boneName] of Object.entries(RIG_SOCKET_BONE)) {
    const found = resolveRigBone(bone, boneName);
    if (found !== null && found !== undefined) {
      out[slot] = { slot, mode: 'bone', bone: found, boneName, transform: RIG_BONE_ATTACH_OFFSET };
      continue;
    }
    const fallback = capsule[slot];
    if (fallback !== undefined) {
      out[slot] = { slot, mode: 'capsule', transform: fallback };
    }
    // Neither a rig bone nor a capsule transform ⇒ slot omitted (no attachment).
  }
  return out;
}
